import { ReactNode } from 'react';
import { startCase } from 'lodash';
import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { coveragesOf } from './eligibility/coverageTabs';
import { VALUE_NAMES } from './constants';
import { COVERAGE_SEQUENCES } from './coverages';
import RevealableSsn from './RevealableSsn';

// The at-a-glance facts under the referral title (UX, 2026-08-04): who the patient is and who
// insures them, so a coordinator doesn't have to open the extraction card to check a DOB.
//
// Every chip is conditional. A referral whose packet never stated a DOB shows no DOB chip rather
// than an empty one, and a referral with no coverages shows no payer chips — the row is a summary
// of what's known, not a form with blanks.
//
// The payer chips read the referral's LIVE coverages, not the `coverages` extraction snapshot.
// The snapshot is whatever the AI last read off the packet, so editing an insurance, adding one, or
// archiving one left these chips showing the old payer indefinitely (OLIO-3141). The extraction
// card still reads the snapshot, and should: that card's job is to show what was read.
//
// The SSN is masked here exactly as it is on the extraction card. The full number lives only in
// the entry form.
export default function PatientSummaryChips({ workflow }: { workflow: Workflow }) {
  // `facetId` reads a value belonging to one coverage. A coverage-scoped value is never on the
  // referral itself, so an unscoped read would miss it entirely.
  const value = (name: string, facetId?: string) => {
    const raw = workflow.valueFor(name, facetId ? { facetId } : undefined)?.value;
    return typeof raw === 'string' && raw ? raw : null;
  };

  const dob = simpleDate(value(VALUE_NAMES.PATIENT_DOB));
  const gender = value(VALUE_NAMES.PATIENT_GENDER);
  const ssn = value(VALUE_NAMES.PATIENT_SSN);

  const chips: { key: string; label: string; text: ReactNode }[] = [
    ...(dob ? [{ key: 'dob', label: 'DOB', text: dob }] : []),
    ...(gender ? [{ key: 'gender', label: 'Gender', text: startCase(gender) }] : []),
    // Masked with a reveal control, through the same component the extraction card uses.
    ...(ssn ? [{ key: 'ssn', label: 'SSN', text: <RevealableSsn ssn={ssn} /> }] : []),
    // One chip per live coverage that has a payer to name — a coverage without its payer has
    // nothing to show here, and belongs on the extraction card where the member id sits.
    //
    // Keyed and labelled by the coverage itself, never by its place in the list: rank IS which
    // insurance it is, so a referral holding only a secondary labels it Secondary and leaves the
    // primary unmentioned rather than promoting it (OLIO-3141). A rank with no declared word gets
    // no chip, the same as before — a label is never invented.
    ...coveragesOf(workflow).flatMap((facet) => {
      const label = startCase(facet.label ?? COVERAGE_SEQUENCES[facet.position] ?? '');
      const payerName = value(VALUE_NAMES.PAYER_NAME, facet.id);
      return label && payerName ? [{ key: `coverage-${facet.id}`, label, text: payerName }] : [];
    }),
  ];

  if (chips.length === 0) return null;

  return (
    <Chips data-testid='patient-summary-chips'>
      {chips.map((chip) => (
        <Chip key={chip.key}>
          <ChipLabel>{chip.label}:</ChipLabel> {chip.text}
        </Chip>
      ))}
    </Chips>
  );
}

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Chip = styled(BodySmall)`
  background: ${colors.black5};
  border-radius: 4px;
  color: ${colors.black100};
  padding: 4px 10px;
  white-space: nowrap;
  font-size: 12px;
`;

const ChipLabel = styled.span`
  color: ${colors.black100};
`;
